/* iter81 — Client-side email typo detection. Mirrors the backend
   (`backend/email_validation.py`) so users get inline "Did you mean
   ...?" hints BEFORE submit, but the backend remains the source of
   truth. */

const COMMON_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'icloud.com', 'me.com', 'aol.com', 'live.com', 'msn.com',
  'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net',
  'protonmail.com', 'proton.me',
  'yahoo.co.uk', 'hotmail.co.uk', 'btinternet.com',
];

const DIRECT_TYPO_MAP = {
  // Gmail
  'gmial.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gamil.com': 'gmail.com',
  'gmali.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com', 'gmaill.com': 'gmail.com', 'gmail.copm': 'gmail.com',
  'gmailcom': 'gmail.com', 'gmaiil.com': 'gmail.com', 'ggmail.com': 'gmail.com',
  'gmail.comm': 'gmail.com', 'gmail.cmo': 'gmail.com',
  // Yahoo
  'yaho.com': 'yahoo.com', 'yhaoo.com': 'yahoo.com', 'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com', 'yahoo.cm': 'yahoo.com', 'yahoo.con': 'yahoo.com',
  'yhoo.com': 'yahoo.com', 'ayhoo.com': 'yahoo.com',
  // Hotmail
  'hotmial.com': 'hotmail.com', 'hotnail.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com', 'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com', 'hotmail.con': 'hotmail.com',
  'hotmaill.com': 'hotmail.com', 'hotmailcom': 'hotmail.com',
  // Outlook
  'outlok.com': 'outlook.com', 'outloo.com': 'outlook.com',
  'outlokk.com': 'outlook.com', 'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com', 'outlook.con': 'outlook.com',
  // iCloud
  'iclod.com': 'icloud.com', 'icould.com': 'icloud.com',
  'icloud.co': 'icloud.com', 'icoud.com': 'icloud.com',
  // AOL
  'aol.co': 'aol.com', 'aoll.com': 'aol.com',
  // Comcast
  'comcast.com': 'comcast.net', 'comcat.net': 'comcast.net',
  // Proton
  'protonmial.com': 'protonmail.com',
};

function levenshteinLE1(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length > b.length) { [a, b] = [b, a]; }
  if (a.length !== b.length) {
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return a.slice(i) === b.slice(i + 1);
    }
    return true;
  }
  let diffs = 0;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) diffs++;
  return diffs <= 1;
}

/**
 * Returns a corrected email if the domain is an obvious typo,
 * otherwise null. Network-free.
 */
export function suggestCorrectEmail(email) {
  if (!email || typeof email !== 'string') return null;
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).toLowerCase().trim();
  if (!domain) return null;
  // Direct lookup
  if (DIRECT_TYPO_MAP[domain]) {
    return `${local}@${DIRECT_TYPO_MAP[domain]}`;
  }
  // Don't second-guess a known good domain
  if (COMMON_DOMAINS.includes(domain)) return null;
  // Levenshtein ≤ 1 fallback
  for (const canon of COMMON_DOMAINS) {
    if (levenshteinLE1(domain, canon)) {
      return `${local}@${canon}`;
    }
  }
  return null;
}

/**
 * Light syntactic check. Returns {ok, error}.
 * (Backend still does the authoritative validation.)
 */
export function isLikelyValidEmail(email) {
  if (!email) return { ok: false };
  const trimmed = email.trim();
  // RFC-ish — local@host.tld with at least one dot in the host
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!re.test(trimmed)) return { ok: false };
  return { ok: true };
}
